/**
 * SimulationControlsBar — playback controls for the flight simulation.
 * Allows selecting controller, start/pause/resume/reset, and playback speed.
 */

import { Play, Pause, X } from "lucide-react";
import { useSimulation } from "@/hooks/useSimulation";
import { Panel } from "@/components/ui";
import { useState } from "react";
import { DefaultConfig } from "@/lib/physics";

interface SimulationControlsBarProps {
  onConfigChange?: () => void;
}

const ALGORITHMS = [
  { value: "hand_rule", label: "Hand Rule", desc: "Deterministic autopilot" },
  { value: "pid", label: "PID Controller", desc: "Cascaded PID guidance" },
] as const;

export function SimulationControlsBar({ onConfigChange }: SimulationControlsBarProps = {}) {
  const { start, pause, resume, reset, isRunning, isPaused, isCompleted } = useSimulation();
  const [algorithm, setAlgorithm] = useState<"hand_rule" | "pid">("hand_rule");
  const [seed, setSeed] = useState<number>(Date.now() % 10000);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);

  const handleStart = () => {
    start(DefaultConfig, algorithm, seed);
  };

  const handleReset = () => {
    reset();
    setSeed(Date.now() % 10000);
  };

  return (
    <Panel title="Simulation Controls" padded={false}>
      <div className="p-3 space-y-4">
        {/* Controller selection */}
        <div className="flex items-center gap-3">
          <span className="text-[0.625rem] text-muted uppercase tracking-wider flex-shrink-0">Guidance</span>
          <div className="flex gap-1 min-w-0 flex-1">
            {ALGORITHMS.map((a) => {
              return (
                <button
                  key={a.value}
                  onClick={() => setAlgorithm(a.value)}
                  className={`flex-1 px-2.5 py-1.5 rounded-sm text-xs font-medium transition ${
                    algorithm === a.value
                      ? "bg-cyan-500 text-black shadow-glow"
                      : "bg-surface text-ink border border-border hover:bg-surface/50 hover:text-cyan-400"
                  }`}
                  title={a.desc}
                >
                  {a.label}
                </button>
              );
            })}
          </div>
          <span className="text-[0.625rem] text-muted flex-shrink-0">
            Seed: {seed}
          </span>
        </div>

        {/* Playback controls */}
        <div className="flex items-center gap-2">
          <button
            onClick={!isRunning && !isPaused ? handleStart : isPaused ? resume : pause}
            disabled={isRunning && isPaused}
            className={`flex items-center gap-2 px-3 py-2 rounded-sm text-xs font-medium transition ${
              !isRunning && !isPaused
                ? "bg-cyan-500 text-black hover:bg-cyan-400 shadow-glow"
                : "bg-surface text-dim border border-border opacity-50 cursor-not-allowed"
            }`}
          >
            <Play size={16} className="fill-black" />
            {isPaused ? "Resume" : isCompleted ? "Run Again" : "Start Simulation"}
          </button>
          <button
            onClick={pause}
            disabled={!isRunning}
            className={`flex items-center gap-2 px-3 py-2 rounded-sm text-xs font-medium transition ${
              !isRunning
                ? "bg-surface text-dim border border-border opacity-50 cursor-not-allowed"
                : "bg-surface border border-border text-amber-400 hover:bg-amber-500/10 hover:border-amber-500/50"
            }`}
          >
            <Pause size={16} /> Pause
          </button>
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-3 py-2 rounded-sm text-xs font-medium bg-surface border border-border text-danger-400 hover:bg-danger-500/10 hover:border-danger-500/50 transition"
          >
            <X size={16} /> Reset
          </button>
        </div>

        {/* Playback speed */}
        <div className="flex items-center gap-2">
          <span className="text-[0.625rem] text-muted">Playback Speed</span>
          <div className="flex-1 min-w-0">
            <input
              type="range"
              min="0.25"
              max="4"
              step="0.25"
              value={playbackSpeed}
              onChange={(e) => setPlaybackSpeed(parseFloat(e.target.value))}
              className="w-full accent-cyan-500"
            />
          </div>
          <span className="text-[0.625rem] text-muted mono">{playbackSpeed.toFixed(2)}×</span>
        </div>

        {/* Status bar */}
        <div className="flex items-center justify-between pt-2 border-t border-border">
          <div className="flex items-center gap-2">
            <span
              className={`w-2 h-2 rounded-full ${
                isRunning ? "bg-cyan-400 animate-pulse-slow" : isPaused ? "bg-amber-500" : "bg-dim"
              }`}
            />
            <span className="text-xs font-mono">
              {isRunning ? "RUNNING" : isPaused ? "PAUSED" : isCompleted ? "COMPLETED" : "IDLE"}
            </span>
          </div>
          {onConfigChange && (
            <button
              onClick={onConfigChange}
              className="px-2.5 py-1.5 rounded-sm text-xs font-medium bg-surface border border-border text-ink hover:bg-surface/50 transition"
            >
              Configuration
            </button>
          )}
        </div>
      </div>
    </Panel>
  );
}

